import React, { useState, useEffect, useMemo } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { adminApiService } from '../../services/adminApi';
import CustomDatePicker from '../CustomDatePicker';

// F4 — recurring schedule builder for admin/club batch bookings.
const DAYS = [
  { id: 'monday', short: 'จ' }, { id: 'tuesday', short: 'อ' }, { id: 'wednesday', short: 'พ' },
  { id: 'thursday', short: 'พฤ' }, { id: 'friday', short: 'ศ' }, { id: 'saturday', short: 'ส' }, { id: 'sunday', short: 'อา' },
];
const DAY_INDEX = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
const TIME_OPTIONS = (() => { const a = []; for (let h = 6; h <= 23; h++) for (const m of ['00', '30']) a.push(`${String(h).padStart(2, '0')}:${m}`); return a; })();
const timeToMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
const fmt = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
const newSlot = () => ({ id: Math.random().toString(36).slice(2), days: [], startTime: '', endTime: '' });

const AdminBooking = () => {
  const { adminUser } = useAdmin();
  const token = adminUser?.idToken;
  const [courts, setCourts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // config (the "top part" — what a template saves)
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [courtIds, setCourtIds] = useState([]);
  const [slots, setSlots] = useState([newSlot()]);
  // per-session (never saved)
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [excluded, setExcluded] = useState(new Set());

  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [saveOpen, setSaveOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');

  useEffect(() => { if (token) load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const load = async () => {
    try {
      setLoading(true);
      const [c, tpls] = await Promise.all([
        adminApiService.getCourts(token),
        adminApiService.getBookingTemplates(token),
      ]);
      setCourts((c || []).filter((x) => x.active !== false));
      setTemplates(tpls || []);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  const courtName = (id) => courts.find((c) => c.id === id)?.name || id;

  // Generate preview sessions (one per date+time, across the selected courts).
  const sessions = useMemo(() => {
    if (!startDate || !endDate || startDate > endDate || courtIds.length === 0) return [];
    const out = [];
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');
    for (const slot of slots) {
      if (!slot.days.length || !slot.startTime || !slot.endTime) continue;
      if (timeToMin(slot.endTime) <= timeToMin(slot.startTime)) continue;
      const idx = slot.days.map((d) => DAY_INDEX[d]);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        if (idx.includes(d.getDay())) {
          const date = fmt(d);
          out.push({ key: `${date}|${slot.startTime}|${slot.endTime}`, date, startTime: slot.startTime, endTime: slot.endTime });
        }
      }
    }
    out.sort((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime));
    return out;
  }, [startDate, endDate, slots, courtIds]);

  const activeSessions = sessions.filter((s) => !excluded.has(s.key));
  const totalBookings = activeSessions.length * courtIds.length;

  const toggleCourt = (id) => setCourtIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleSlotDay = (sid, day) => setSlots((p) => p.map((s) => (s.id === sid ? { ...s, days: s.days.includes(day) ? s.days.filter((x) => x !== day) : [...s.days, day] } : s)));
  const updateSlot = (sid, f, v) => setSlots((p) => p.map((s) => (s.id === sid ? { ...s, [f]: v } : s)));
  const toggleExclude = (key) => setExcluded((p) => { const n = new Set(p); if (n.has(key)) n.delete(key); else n.add(key); return n; });

  const resetConfig = () => { setContactName(''); setContactPhone(''); setCourtIds([]); setSlots([newSlot()]); setStartDate(''); setEndDate(''); setExcluded(new Set()); setResult(null); };
  const loadTemplate = (id) => {
    setSelectedTemplateId(id);
    const tpl = templates.find((x) => x.id === id);
    if (!tpl) { resetConfig(); return; }
    setContactName(tpl.contactName || '');
    setContactPhone(tpl.contactPhone || '');
    setCourtIds(tpl.courtIds || []);
    setSlots((tpl.slots || []).map((s) => ({ id: Math.random().toString(36).slice(2), days: s.days || [], startTime: s.startTime || '', endTime: s.endTime || '' })));
    setStartDate(''); setEndDate(''); setExcluded(new Set()); setResult(null); setError(null); // dates blank per spec
  };
  const saveTemplate = async () => {
    if (!templateName.trim()) return;
    try {
      const created = await adminApiService.createBookingTemplate(token, {
        name: templateName.trim(), contactName, contactPhone, courtIds,
        slots: slots.filter((s) => s.days.length && s.startTime && s.endTime).map(({ days, startTime, endTime }) => ({ days, startTime, endTime })),
      });
      setTemplates((p) => [...p, created].sort((a, b) => a.name.localeCompare(b.name)));
      setSaveOpen(false); setTemplateName('');
    } catch (e) { setError(e.message); }
  };
  const deleteTemplate = async () => {
    if (!selectedTemplateId || !window.confirm('ลบเทมเพลตนี้?')) return;
    try { await adminApiService.deleteBookingTemplate(token, selectedTemplateId); setTemplates((p) => p.filter((t) => t.id !== selectedTemplateId)); setSelectedTemplateId(''); }
    catch (e) { setError(e.message); }
  };

  const submit = async () => {
    setError(null); setResult(null);
    if (!contactName.trim() || !contactPhone.trim()) return setError('กรุณากรอกชื่อและเบอร์โทรลูกค้า');
    if (!startDate || !endDate) return setError('กรุณาเลือกช่วงวันที่');
    if (startDate > endDate) return setError('วันที่เริ่มต้องอยู่ก่อนวันที่สิ้นสุด');
    if (courtIds.length === 0) return setError('กรุณาเลือกสนามอย่างน้อย 1 สนาม');
    if (totalBookings === 0) return setError('ไม่มีการจองที่จะสร้าง — เพิ่มช่วงเวลาและวันในสัปดาห์');
    if (totalBookings > 50 && !window.confirm(`ยืนยันสร้าง ${totalBookings} การจอง?`)) return;

    const bookings = [];
    for (const s of activeSessions) for (const courtId of courtIds) {
      bookings.push({ courtId, date: s.date, startTime: s.startTime, endTime: s.endTime, duration: timeToMin(s.endTime) - timeToMin(s.startTime) });
    }
    try {
      setSubmitting(true);
      const res = await adminApiService.createMultiCourtBatch(token, { nickname: contactName.trim(), mobilePhone: contactPhone.trim() }, bookings);
      setResult(res);
      setExcluded(new Set());
    } catch (e) { setError(e.message); } finally { setSubmitting(false); }
  };

  if (loading) return <div className="text-center py-10 text-gray-500">กำลังโหลด...</div>;

  return (
    <div className="max-w-3xl">
      <h2 className="text-xl font-bold text-gray-900 mb-1">จองแทนลูกค้า (สโมสร / ประจำ)</h2>
      <p className="text-sm text-gray-500 mb-5">สร้างการจองซ้ำหลายวัน หลายสนาม พร้อมกัน — บันทึกเป็นเทมเพลตต่อสโมสรได้</p>

      {/* Template bar */}
      <div className="flex flex-wrap items-end gap-3 mb-6 p-4 bg-gray-50 rounded-xl">
        <div className="flex-1 min-w-[180px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">เทมเพลตสโมสร</label>
          <select value={selectedTemplateId} onChange={(e) => loadTemplate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white">
            <option value="">— เริ่มใหม่ —</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <button onClick={() => setSaveOpen(true)} className="px-4 py-2 bg-white border border-blue-200 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50">💾 บันทึกเป็นเทมเพลต</button>
        {selectedTemplateId && <button onClick={deleteTemplate} className="px-3 py-2 text-sm text-red-600 hover:text-red-700">ลบเทมเพลต</button>}
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}
      {result && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-800 rounded-lg text-sm">
          ✅ สร้างการจองสำเร็จ {result.createdCount} รายการ
          {result.skipped?.length > 0 && <span className="text-amber-700"> · ข้าม {result.skipped.length} รายการ (ชนกับการจองเดิม)</span>}
        </div>
      )}

      {/* Contact */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">ชื่อลูกค้า / สโมสร</label>
          <input value={contactName} onChange={(e) => setContactName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="เช่น สโมสร A" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">เบอร์โทร</label>
          <input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="08x-xxx-xxxx" />
        </div>
      </div>

      {/* Date range */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">วันที่เริ่ม</label>
          <CustomDatePicker value={startDate} onChange={(d) => setStartDate(d)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">วันที่สิ้นสุด</label>
          <CustomDatePicker value={endDate} min={startDate || undefined} onChange={(d) => setEndDate(d)} />
        </div>
      </div>

      {/* Courts multi-select */}
      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-700 mb-2">สนาม (เลือกได้หลายสนาม)</label>
        <div className="flex flex-wrap gap-2">
          {courts.map((c) => (
            <button key={c.id} type="button" onClick={() => toggleCourt(c.id)}
              className={`px-3 py-1.5 rounded-full text-sm border ${courtIds.includes(c.id) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}>
              {c.name}
            </button>
          ))}
        </div>
      </div>

      {/* Weekly slots */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">ตารางประจำสัปดาห์</label>
          <button onClick={() => setSlots((p) => [...p, newSlot()])} className="text-sm text-blue-600 font-medium hover:text-blue-700">+ เพิ่มช่วง</button>
        </div>
        <div className="space-y-3">
          {slots.map((slot) => (
            <div key={slot.id} className="p-3 border border-gray-200 rounded-xl">
              <div className="flex flex-wrap items-center gap-1.5 mb-2">
                {DAYS.map((d) => (
                  <button key={d.id} type="button" onClick={() => toggleSlotDay(slot.id, d.id)}
                    className={`w-9 h-9 rounded-full text-sm font-medium ${slot.days.includes(d.id) ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                    {d.short}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <select value={slot.startTime} onChange={(e) => updateSlot(slot.id, 'startTime', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="">เริ่ม</option>{TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <span className="text-gray-400">–</span>
                <select value={slot.endTime} onChange={(e) => updateSlot(slot.id, 'endTime', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="">สิ้นสุด</option>{TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                {slots.length > 1 && <button onClick={() => setSlots((p) => p.filter((s) => s.id !== slot.id))} className="ml-auto text-sm text-red-600 hover:text-red-700">ลบช่วง</button>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Preview */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">ตัวอย่าง</label>
          <span className="text-sm font-semibold text-gray-900">
            {totalBookings.toLocaleString()} การจอง <span className="text-gray-400 font-normal">({activeSessions.length} วัน × {courtIds.length} สนาม)</span>
          </span>
        </div>
        {sessions.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm border border-dashed border-gray-200 rounded-lg">เลือกวันที่ สนาม และช่วงเวลาเพื่อดูตัวอย่าง</div>
        ) : (
          <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
            {sessions.map((s) => {
              const ex = excluded.has(s.key);
              return (
                <div key={s.key} className={`flex items-center justify-between px-3 py-2 text-sm ${ex ? 'opacity-40' : ''}`}>
                  <span className={ex ? 'line-through' : ''}>{s.date} · {s.startTime}–{s.endTime} · {courtIds.length} สนาม</span>
                  <button onClick={() => toggleExclude(s.key)} className={`text-xs font-medium ${ex ? 'text-blue-600' : 'text-red-500 hover:text-red-600'}`}>
                    {ex ? 'ใส่กลับ' : 'เอาออก'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <button onClick={submit} disabled={submitting || totalBookings === 0}
        className={`w-full py-3.5 rounded-xl font-semibold text-white transition ${submitting || totalBookings === 0 ? 'bg-gray-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
        {submitting ? 'กำลังสร้าง...' : `สร้างการจองทั้งหมด (${totalBookings.toLocaleString()})`}
      </button>

      {/* Save-template modal */}
      {saveOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setSaveOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-3">บันทึกเป็นเทมเพลต</h3>
            <p className="text-xs text-gray-500 mb-3">บันทึกเฉพาะการตั้งค่า (ลูกค้า สนาม ช่วงเวลา) — ไม่รวมวันที่และตัวอย่าง</p>
            <input value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="ชื่อเทมเพลต เช่น สโมสร A" className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setSaveOpen(false)} className="px-4 py-2 text-sm text-gray-600">ยกเลิก</button>
              <button onClick={saveTemplate} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium">บันทึก</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBooking;
